import { HealthProfessional, CallRequestData, CallConfirmation } from '../types';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
const API_TIMEOUT = 10000; // 10 seconds
const COGNITO_CLIENT_ID = '2on4e61mjbiqc4if6ua04g9cad';

// Custom error class for API errors
export class ApiError extends Error {
  public code?: string;
  public status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

// Helper to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch {
    return true;
  }
};

// Helper to force logout
const forceLogout = () => {
  console.log('🚪 Token expired - forcing logout...');
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = '/login';
};

// Refresh the access token using refresh token
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (!refreshToken) {
    console.log('No refresh token available - forcing logout');
    forceLogout();
    return null;
  }

  // Check if refresh token is expired
  if (isTokenExpired(refreshToken)) {
    console.log('🔒 Refresh token expired - forcing logout');
    forceLogout();
    return null;
  }

  try {
    console.log('🔄 Refreshing access token...');
    
    const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
      },
      body: JSON.stringify({
        "AuthFlow": "REFRESH_TOKEN_AUTH",
        "ClientId": COGNITO_CLIENT_ID,
        "AuthParameters": {
          "REFRESH_TOKEN": refreshToken
        }
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('Token refresh failed:', data);
      console.log('🔒 Refresh failed - forcing logout');
      forceLogout();
      return null;
    }

    const newAccessToken = data.AuthenticationResult?.AccessToken;
    const newIdToken = data.AuthenticationResult?.IdToken;
    
    if (newAccessToken) {
      console.log('✅ Token refreshed successfully');
      localStorage.setItem('authToken', newAccessToken);
      if (newIdToken) {
        localStorage.setItem('idToken', newIdToken);
      }
      return newAccessToken;
    }
    
    console.log('🔒 No new token received - forcing logout');
    forceLogout();
    return null;
  } catch (error) {
    console.error('Token refresh error:', error);
    console.log('🔒 Refresh error - forcing logout');
    forceLogout();
    return null;
  }
};

// Get valid token, refreshing if expired
const getValidToken = async (): Promise<string | null> => {
  let authToken = localStorage.getItem('authToken');
  
  if (!authToken) {
    console.log('🔒 No auth token - forcing logout');
    forceLogout();
    return null;
  }
  
  // Check if token is expired
  if (isTokenExpired(authToken)) {
    console.log('🔄 Token expired, attempting refresh...');
    authToken = await refreshAccessToken();
    
    // If refresh failed, forceLogout() was already called in refreshAccessToken
    if (!authToken) {
      return null;
    }
  }
  
  return authToken;
};

// Generic API request function with automatic token refresh
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  retries: number = 1
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Get valid token (refreshes if expired)
  let authToken = await getValidToken();
  
  if (process.env.NODE_ENV === 'development') {
    console.log('API Request:', endpoint);
    console.log('Auth Token:', authToken ? `${authToken.substring(0, 20)}...` : 'No token');
  }
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
      ...options.headers,
    },
    ...options,
  };

  // Add timeout to the request
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
  
  try {
    const response = await fetch(url, {
      ...defaultOptions,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (process.env.NODE_ENV === 'development') {
      console.log('API Response Status:', response.status);
    }

    if (!response.ok) {
      // Handle 401/403 - SILENTLY refresh token and retry OR redirect to login
      if (response.status === 401 || response.status === 403) {
        console.log('🔄 Auth error detected, attempting silent token refresh...');
        
        if (retries > 0) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            console.log('✅ Token refreshed successfully, retrying request silently...');
            return apiRequest<T>(endpoint, options, 0); // Retry with new token
          }
        }
        
        // Refresh failed or no retries left - SILENTLY redirect to login
        console.log('🔒 Token refresh failed, redirecting to login (no error shown to user)');
        localStorage.clear();
        sessionStorage.clear();
        
        // Redirect without throwing error (silent redirect)
        window.location.href = '/login';
        
        // Return a promise that never resolves (page will redirect anyway)
        return new Promise(() => {});
      }
      
      // For non-auth errors, throw error normally
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch {
        // Use default error message
      }
      
      throw new ApiError(errorMessage, undefined, response.status);
    }

    const data: T = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Handle network errors and timeouts
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new ApiError('Request timeout - please try again');
      }
      
      // Retry logic for network errors
      if (retries > 0 && !error.message.includes('HTTP')) {
        console.warn(`API request failed, retrying... (${retries} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
        return apiRequest<T>(endpoint, options, retries - 1);
      }
    }
    
    throw error;
  }
}

// Health Professional API Services
export const healthProfessionalService = {
  /**
   * Fetch all health professionals
   */
  async getAll(): Promise<HealthProfessional[]> {
    return apiRequest<HealthProfessional[]>('/health-professionals');
  },

  /**
   * Fetch health professionals filtered by specialties
   */
  async getBySpecialties(specialties: string[]): Promise<HealthProfessional[]> {
    const params = new URLSearchParams();
    specialties.forEach(specialty => params.append('specialty', specialty));
    
    return apiRequest<HealthProfessional[]>(`/health-professionals?${params.toString()}`);
  },

  /**
   * Get a specific health professional by ID
   */
  async getById(id: string): Promise<HealthProfessional> {
    return apiRequest<HealthProfessional>(`/health-professionals/${id}`);
  },

  /**
   * Get available health professionals (those available today)
   */
  async getAvailable(): Promise<HealthProfessional[]> {
    return apiRequest<HealthProfessional[]>('/health-professionals?available=true');
  },

  /**
   * Create a new health professional (admin function)
   */
  async create(professionalData: Omit<HealthProfessional, 'id' | 'createdAt' | 'updatedAt'>): Promise<HealthProfessional> {
    return apiRequest<HealthProfessional>('/health-professionals', {
      method: 'POST',
      body: JSON.stringify(professionalData),
    });
  },

  /**
   * Update a health professional (admin function)
   */
  async update(id: string, updates: Partial<HealthProfessional>): Promise<HealthProfessional> {
    return apiRequest<HealthProfessional>(`/health-professionals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete a health professional (admin function)
   */
  async delete(id: string): Promise<void> {
    await apiRequest<void>(`/health-professionals/${id}`, {
      method: 'DELETE',
    });
  }
};

// Call Request API Services
export const callRequestService = {
  /**
   * Submit a new call request
   */
  async submit(
    healthProfessionalId: string, 
    requestData: CallRequestData
  ): Promise<CallConfirmation> {
    console.log('🌍 Submitting call request with timezone:', requestData.timezone);
    
    const payload = {
      healthProfessionalId,
      symptoms: requestData.symptoms,
      patientPhone: requestData.patientPhone,
      patientName: requestData.patientName,
      patientEmail: requestData.patientEmail, // NEW REQUIRED FIELD
      requestedDate: requestData.requestedDate, // NEW REQUIRED FIELD
      requestedTime: requestData.requestedTime, // NEW REQUIRED FIELD
      requestedTimeRangeStart: requestData.requestedTimeRangeStart, // UTC time range start
      requestedTimeRangeEnd: requestData.requestedTimeRangeEnd, // UTC time range end
      timezone: requestData.timezone, // ⚠️ CRITICAL: Patient's IANA timezone
      meetingDuration: requestData.meetingDuration, // NEW REQUIRED FIELD
      age: requestData.age,
      gender: requestData.gender,
      medicalHistory: requestData.medicalHistory,
      currentMedications: requestData.currentMedications,
      needsMedicalCertificate: requestData.needsMedicalCertificate,
      urgencyLevel: requestData.urgencyLevel,
      preferredCallTime: requestData.preferredCallTime,
      additionalNotes: requestData.additionalNotes,
      paymentIntentId: requestData.paymentIntentId, // Payment intent if paid
      discountCode: requestData.discountCode, // Discount code if applied
      language: requestData.language || 'english' // Default to english
    };
    
    console.log('📤 Payload being sent to backend:', payload);

    const response = await apiRequest<{
      requestId: string;
      message: string;
      estimatedCallbackTime: string;
      healthProfessionalName: string;
      createdAt: string;
    }>('/call-requests', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    // Transform the response to match CallConfirmation interface
    return {
      requestId: response.requestId,
      estimatedCallbackTime: response.estimatedCallbackTime,
      symptoms: requestData.symptoms,
      preparationTips: [
        'Check your email for the Google Meet link',
        'Test your camera and microphone before the meeting',
        'Find a quiet, private space with good lighting',
        'Have a list of current medications ready',
        'Prepare any questions you want to ask',
        'Keep a pen and paper nearby for notes'
      ],
      confirmationMessage: `Thank you ${requestData.patientName || ''}! Your consultation request has been submitted successfully. You'll receive a Google Meet link via email for your video consultation ${response.estimatedCallbackTime.toLowerCase()}.`
    };
  },

  /**
   * Get call request details by ID
   */
  async getById(requestId: string): Promise<CallConfirmation> {
    console.log('📞 Fetching request details for ID:', requestId);
    
    try {
      const response = await apiRequest<any>(`/call-requests/${requestId}`);
      console.log('📦 Raw response from GET /call-requests:', response);
      
      // Transform backend response to CallConfirmation format
      return {
        requestId: response.id || response.requestId || requestId,
        estimatedCallbackTime: response.estimatedCallbackTime || 'within 24 hours',
        symptoms: response.symptoms || [],
        preparationTips: [
          'Check your email for the Google Meet link',
          'Test your camera and microphone before the meeting',
          'Find a quiet, private space with good lighting',
          'Have a list of current medications ready',
          'Prepare any questions you want to ask',
          'Keep a pen and paper nearby for notes'
        ],
        confirmationMessage: response.confirmationMessage || 'Thank you! Your consultation request has been submitted successfully. You\'ll receive a Google Meet link via email.'
      };
    } catch (error) {
      console.error('❌ Error fetching request details:', error);
      throw error;
    }
  },

  /**
   * Update call request status (for internal use)
   */
  async updateStatus(requestId: string, status: string): Promise<void> {
    await apiRequest<void>(`/call-requests/${requestId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }
};

// Utility functions for error handling
export const handleApiError = (error: unknown): string => {
  if (error instanceof ApiError) {
    return error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

// Doctor Dashboard API Services
export const doctorService = {
  /**
   * Get dashboard statistics for a doctor
   */
  async getStats(doctorId: string): Promise<{
    todayRequests: number;
    pendingRequests: number;
    completedToday: number;
    averageCallTime: number;
    weeklyStats: Array<{
      date: string;
      totalCalls: number;
      completedCalls: number;
      averageDuration: number;
      revenue: number;
    }>;
    monthlyRevenue: number;
  }> {
    return apiRequest(`/doctors/${doctorId}/stats`);
  },

  /**
   * Get call requests for a doctor (marketplace model - shows ALL requests)
   */
  async getCallRequests(doctorId: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const queryString = queryParams.toString();
    // In marketplace model, any doctor can see all requests
    const endpoint = `/doctors/${doctorId}/call-requests${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiRequest<{requests: any[], totalCount: number}>(endpoint);
    return response.requests || [];
  },

  /**
   * Get ALL available requests (marketplace model)
   * Note: Backend returns all pending requests regardless of doctorId in URL
   */
  async getAllAvailableRequests(doctorId: string): Promise<any[]> {
    const response = await apiRequest<{requests: any[], totalCount: number}>(`/doctors/${doctorId}/call-requests?status=pending`);
    
    // Extract the requests array from the response
    return response.requests || [];
  },

  /**
   * Get doctor's active requests (requests they've picked up)
   */
  async getMyActiveRequests(doctorId: string): Promise<any[]> {
    const response = await apiRequest<{requests: any[], totalCount: number}>(`/doctors/${doctorId}/call-requests?status=in_progress`);
    return response.requests || [];
  },

  /**
   * Get doctor's completed requests for today
   */
  async getMyCompletedToday(doctorId: string): Promise<any[]> {
    const today = new Date().toISOString().split('T')[0];
    const response = await apiRequest<{requests: any[], totalCount: number}>(`/doctors/${doctorId}/call-requests?status=completed&date=${today}`);
    return response.requests || [];
  },

  /**
   * Pick up a request (Uber-style claiming)
   */
  async pickUpRequest(requestId: string): Promise<{ message: string }> {
    return apiRequest(`/call-requests/${requestId}/start`, {
      method: 'POST',
      body: JSON.stringify({
        actualCallbackTime: new Date().toISOString()
      }),
    });
  },

  /**
   * Start a call (same as pickUpRequest, kept for compatibility)
   */
  async startCall(requestId: string): Promise<{ message: string }> {
    return apiRequest(`/call-requests/${requestId}/start`, {
      method: 'POST',
      body: JSON.stringify({
        actualCallbackTime: new Date().toISOString()
      }),
    });
  },

  /**
   * Complete a call
   */
  async completeCall(requestId: string, data: {
    callDuration: number;
    callNotes: string;
    patientRecommendation: string;
    medicalCertificateIssued: boolean;
  }): Promise<{ message: string }> {
    return apiRequest(`/call-requests/${requestId}/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Cancel a call
   */
  async cancelCall(requestId: string, cancellationReason: string): Promise<{ message: string }> {
    return apiRequest(`/call-requests/${requestId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ cancellationReason }),
    });
  }
};

// Promo Code API Services
export const promoCodeService = {
  /**
   * Admin: Create a new promo code
   */
  async create(data: {
    code: string;
    discountPercent: number;
    quantityIssued: number;
    validFrom: string;
    validTo: string;
  }): Promise<any> {
    return apiRequest('/admin/promo-codes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Admin: List all promo codes
   */
  async list(status?: 'active' | 'all'): Promise<{ promoCodes: any[] }> {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/admin/promo-codes${params}`);
  },

  /**
   * Admin: Get promo code details
   */
  async getDetails(code: string): Promise<any> {
    return apiRequest(`/admin/promo-codes/${code}`);
  },

  /**
   * Admin: Update promo code
   */
  async update(code: string, updates: {
    isEnabled?: boolean;
    validTo?: string;
    quantityIssued?: number;
  }): Promise<any> {
    return apiRequest(`/admin/promo-codes/${code}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Customer: Validate promo code
   */
  async validate(code: string): Promise<{
    valid: boolean;
    code: string;
    discountPercent: number;
    message: string;
  }> {
    return apiRequest('/payments/validate-discount', {
      method: 'POST',
      body: JSON.stringify({ 
        code: code
        // amount is optional - backend doesn't require it
      }),
    });
  },
};

// Health check function
export const healthCheck = async (): Promise<boolean> => {
  try {
    await apiRequest<{ status: string }>('/health');
    return true;
  } catch {
    return false;
  }
};

// Export all services
export const apiService = {
  healthProfessionals: healthProfessionalService,
  callRequests: callRequestService,
  doctors: doctorService,
  promoCodes: promoCodeService,
  healthCheck,
  handleApiError,
};

export default apiService;